import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { verifyOTP } from '../../store/slices/authSlice';

const OTPVerification = ({ user, onVerificationSuccess, onBack }) => {
  const dispatch = useDispatch();
  const { isLoading, otpError } = useSelector((state) => state.auth);
  const [otp, setOtp] = useState('');

  const handleVerifyOTP = () => {
    if (!otp || otp.length !== 6) {
      return; // The disabled state of the button should prevent this
    }
    dispatch(verifyOTP({ user, otpCode: otp }));
  };

  // Clear OTP input on error
  useEffect(() => {
    if (otpError) {
      setOtp('');
    }
  }, [otpError]);

  return (
    <div className="flex justify-center items-center min-h-screen bg-gradient-to-br from-[#f5f7fa] to-[#c3cfe2] p-4 md:p-5 font-sans">
      <div className="bg-white rounded-2xl shadow-2xl p-6 md:p-10 max-w-[450px] w-full text-center relative overflow-hidden animate-slideInUp border border-white/50 mx-auto">

        {/* Loading Overlay */}
        {isLoading && (
          <div className="absolute inset-0 bg-white/95 flex flex-col justify-center items-center z-10 backdrop-blur-sm transition-all duration-300">
            <div className="w-10 h-10 md:w-12 md:h-12 border-4 border-[#f3f3f3] border-t-[#004aad] rounded-full animate-spin mb-4"></div>
            <div className="text-[#004aad] text-base md:text-lg font-semibold mb-2">Verifying OTP...</div>
            <div className="text-gray-500 text-xs md:text-sm">Please wait...</div>
          </div>
        )}

        <div className="mb-6 md:mb-8">
          <div className="w-12 h-12 md:w-16 md:h-16 bg-blue-50 rounded-full flex items-center justify-center mx-auto mb-4 md:mb-6">
            <svg className="w-6 h-6 md:w-8 md:h-8 text-[#004aad]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
          </div>
          <h2 className="text-xl md:text-2xl font-bold text-gray-800 mb-2">Verify Your Identity</h2>
          <p className="text-gray-600 text-sm md:text-base leading-relaxed">
            We've sent a 6-digit code to <br />
            <span className="font-semibold text-gray-800 break-all">{user.email}</span>
          </p>
        </div>

        <div className="flex flex-col gap-4 md:gap-5">
          <div className="relative">
            <input
              type="text"
              value={otp}
              onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
              placeholder="Enter 6-digit OTP"
              className="w-full py-3 md:py-4 px-4 md:px-5 text-xl md:text-2xl text-center tracking-[0.3em] md:tracking-[0.5em] font-bold text-gray-800 bg-gray-50 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-[#004aad] focus:bg-white focus:ring-4 focus:ring-blue-500/10 transition-all duration-300 placeholder:tracking-normal placeholder:text-gray-400 placeholder:font-normal placeholder:text-base md:placeholder:text-lg"
              maxLength="6"
              disabled={isLoading}
              autoFocus
            />
          </div>

          {otpError && (
            <div className="bg-red-50 text-red-600 px-3 py-2 md:px-4 md:py-3 rounded-lg text-xs md:text-sm font-medium border border-red-100 flex items-center justify-center gap-2 animate-pulse">
              <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              {otpError}
            </div>
          )}

          <div className="flex flex-col gap-3 mt-2">
            <button
              onClick={handleVerifyOTP}
              disabled={isLoading || otp.length !== 6}
              className="w-full py-3 md:py-3.5 px-6 bg-[#004aad] text-white rounded-xl text-sm md:text-base font-semibold shadow-lg shadow-blue-900/20 hover:bg-[#003c8f] hover:shadow-blue-900/30 hover:-translate-y-0.5 active:translate-y-0 disabled:opacity-60 disabled:cursor-not-allowed disabled:transform-none transition-all duration-200"
            >
              {isLoading ? 'Verifying...' : 'Verify OTP'}
            </button>

            <button
              onClick={onBack}
              className="w-full py-3 md:py-3.5 px-6 bg-white text-gray-600 border border-gray-200 rounded-xl text-sm md:text-base font-medium hover:bg-gray-50 hover:text-gray-800 hover:border-gray-300 transition-all duration-200"
              disabled={isLoading}
            >
              Back to Login
            </button>
          </div>

          <div className="mt-4 md:mt-6 pt-4 md:pt-6 border-t border-gray-100">
            <p className="text-gray-500 text-xs md:text-sm flex items-center justify-center gap-2">
              <svg className="w-3 h-3 md:w-4 md:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              Enter the 6-digit OTP sent to your email
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OTPVerification;
